import { FieldPath, UseFormReturn } from "react-hook-form";

import { OptionalLabel } from "@/components/OptionalLabel";
import { InfoTooltip } from "@/components/form/InfoTooltip";
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";

import { misc } from "@/lib/misc";
import { cn } from "@/lib/utils";

export const FormNumberInput = <T extends Record<string, unknown>>({
  allowDecimal,
  className,
  description,
  disabled,
  fieldName,
  form,
  infoTooltip,
  inputClassName,
  label,
  maxLength,
  onKeyDown,
  optional,
  placeholder,
}: {
  allowDecimal?: boolean;
  className?: string;
  description?: string;
  disabled?: boolean;
  fieldName: FieldPath<T>;
  form: UseFormReturn<T>;
  infoTooltip?: string;
  inputClassName?: string;
  label?: string;
  maxLength?: number;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  optional?: boolean;
  placeholder?: string;
}) => {
  return (
    <FormField
      control={form.control}
      name={fieldName}
      render={({ field }) => (
        <FormItem className={cn("text-slate-700", className)}>
          {label && (
            <FormLabel className="inline-flex items-center gap-1">
              {label}
              {optional && <OptionalLabel className="ml-1.5" />}
              {infoTooltip && <InfoTooltip content={infoTooltip} />}
            </FormLabel>
          )}
          <FormControl>
            <Input
              {...field}
              className={inputClassName}
              disabled={disabled}
              maxLength={maxLength}
              onKeyDown={(e) => {
                if (allowDecimal) {
                  misc.enforceCurrencyInput(e);
                } else {
                  misc.enforceNumberInput(e);
                }
                onKeyDown?.(e);
              }}
              placeholder={placeholder}
              // The `?? ""` fallback is required because without it, submitting
              // forms with number inputs would fail when field.value is
              // undefined before form initialization.
              value={(field.value as string | number | null) ?? ""}
            />
          </FormControl>
          {description && <FormDescription>{description}</FormDescription>}
          <FormMessage />
        </FormItem>
      )}
    />
  );
};
